import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { withError } from '@/react-tools/react-query';

import { CustomResourceDefinition } from '../types';

import { queryKeys } from './query-keys';

export function useCustomResourceDefinition(
  environmentId: EnvironmentId,
  name: string,
  options?: { autoRefreshRate?: number; yaml?: boolean }
) {
  return useQuery(
    queryKeys.customResourceDefinition(environmentId, name, options?.yaml),
    () => getCustomResourceDefinition(environmentId, name, options?.yaml),
    {
      enabled: !!environmentId && !!name,
      ...withError('Unable to retrieve custom resource definition'),
      refetchInterval() {
        return options?.autoRefreshRate ?? false;
      },
    }
  );
}

async function getCustomResourceDefinition(
  environmentId: EnvironmentId,
  name: string,
  yaml?: boolean
) {
  try {
    const { data } = await axios.get<CustomResourceDefinition>(
      `/kubernetes/${environmentId}/customresourcedefinitions/${name}`,
      {
        params: {
          format: yaml ? 'yaml' : 'json',

          // this logic is to get the latest YAML response
          // axios-cache-adapter looks for the response headers to determine if the response should be cached
          // to avoid writing requestInterceptor, adding a query param to the request url
          ...(yaml && {
            _: Date.now(),
          }),
        },
        headers: { Accept: yaml ? 'application/yaml' : 'application/json' },
      }
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to retrieve custom resource definition');
  }
}
